import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Loader2, Trash2, Pencil, X } from "lucide-react";

const field = "w-full bg-black/50 border border-white/10 px-4 py-2.5 text-bone focus:outline-none focus:border-gold/50 text-sm";
const lbl = "text-xs uppercase tracking-widest text-bone/60 mb-1.5 block";

export function ContentManager({ endpoint, heading, description, fields, blank, toPayload, fromItem, primary, secondary, testid }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(endpoint).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const reset = () => { setF(blank); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = toPayload ? toPayload(f) : f;
    try {
      if (editingId) { await api.put(`${endpoint}/${editingId}`, payload); toast.success("Updated"); }
      else { await api.post(endpoint, payload); toast.success("Added"); }
      reset(); load();
    } catch { toast.error(editingId ? "Update failed" : "Failed to add"); }
    finally { setBusy(false); }
  };

  const edit = (item) => {
    setF(fromItem ? fromItem(item) : { ...blank, ...item });
    setEditingId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Deleted"); if (editingId === id) reset(); load(); }
    catch { toast.error("Delete failed"); }
  };

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  return (
    <div data-testid={testid}>
      <h2 className="font-serif text-2xl text-bone mb-2 flex items-center gap-2">{heading}</h2>
      {description && <p className="text-bone/50 text-sm mb-6">{description}</p>}

      <form onSubmit={submit} className="bg-surface border border-white/10 p-8 space-y-5 max-w-2xl mb-10" data-testid={`${testid}-form`}>
        {editingId && (
          <div className="flex items-center justify-between bg-gold/10 border border-gold/30 px-4 py-2 text-sm text-gold">
            <span>Editing existing item</span>
            <button type="button" onClick={reset} className="flex items-center gap-1 hover:text-bone" data-testid={`${testid}-cancel-edit`}><X size={14} /> Cancel</button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          {fields.map((fd) => {
            if (fd.showIf && !fd.showIf(f)) return null;
            return (
              <div key={fd.key} className={fd.full || fd.type === "textarea" ? "col-span-2" : ""}>
                <label className={lbl}>{fd.label}</label>
                {fd.type === "textarea" ? (
                  <textarea value={f[fd.key] ?? ""} onChange={(e) => set(fd.key, e.target.value)} className={field} rows={fd.rows || 3} placeholder={fd.placeholder} required={fd.required} data-testid={`${testid}-${fd.key}`} />
                ) : fd.type === "select" ? (
                  <select value={f[fd.key] ?? ""} onChange={(e) => set(fd.key, e.target.value)} className={field} data-testid={`${testid}-${fd.key}`}>
                    {fd.options.map((o) => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={fd.type || "text"} step={fd.type === "number" ? "any" : undefined} value={f[fd.key] ?? ""} onChange={(e) => set(fd.key, e.target.value)} className={field} placeholder={fd.placeholder} required={fd.required} data-testid={`${testid}-${fd.key}`} />
                )}
                {fd.hint && <p className="text-bone/40 text-xs mt-1">{fd.hint}</p>}
                {fd.preview && fd.preview(f[fd.key])}
              </div>
            );
          })}
        </div>
        <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-gold text-black px-6 py-2.5 text-sm font-medium hover:bg-gold/90 disabled:opacity-50 transition-colors" data-testid={`${testid}-submit`}>
          {busy && <Loader2 size={16} className="animate-spin" />} {editingId ? "Save Changes" : "Add"}
        </button>
      </form>

      <h3 className="font-serif text-xl text-bone mb-3">Existing ({items.length})</h3>
      {loading ? (
        <div className="text-bone/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading...</div>
      ) : items.length === 0 ? (
        <p className="text-bone/50 text-sm" data-testid={`${testid}-empty`}>Nothing yet. Add your first item above.</p>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-3 p-3 bg-surface text-sm" data-testid={`${testid}-row`}>
              <div className="flex-1 min-w-0">
                <p className="text-bone truncate">{primary(it)}</p>
                {secondary && <p className="text-bone/40 text-xs truncate">{secondary(it)}</p>}
              </div>
              <button onClick={() => edit(it)} className="text-bone/50 hover:text-gold" data-testid={`${testid}-edit`}><Pencil size={15} /></button>
              <button onClick={() => remove(it.id)} className="text-bone/50 hover:text-red-400" data-testid={`${testid}-delete`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
